<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { RouterView, useRouter } from 'vue-router';
import AppHeader from '@/components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import CartPopover from '@/components/CartPopover.vue';
import { useAuthStore } from '@/stores/auth';
import { useFavoritesStore } from '@/stores/favorites';

const auth = useAuthStore();
const favorites = useFavoritesStore();
const router = useRouter();

// One-shot toast surfaced when the Steam callback redirects back with an
// auth_error query param (e.g. steam_already_linked).
const toast = ref<string | null>(null);

// Hydrate the favorites id-set once the user is known, drop it on logout.
// `idsLoaded` gates the fetch so we don't re-request after every navigation.
watch(
  () => auth.user?.id,
  (next) => {
    if (next && !favorites.idsLoaded) favorites.loadIds();
    if (!next) favorites.reset();
  },
);

onMounted(() => {
  if (!auth.initialized) {
    auth.fetchUser();
  }

  const params = new URLSearchParams(window.location.search);
  const err = params.get('auth_error');
  if (err) {
    toast.value =
      err === 'steam_already_linked'
        ? 'This Steam account is already linked to a different JuicySkins user.'
        : err === 'session_expired'
          ? 'Your session expired — please sign in again, then connect Steam from your profile.'
          : 'Steam sign-in failed. Try again or contact support.';
    // Strip the param so reload doesn't re-show the toast.
    params.delete('auth_error');
    params.delete('logged_in');
    const cleaned = params.toString();
    router.replace(window.location.pathname + (cleaned ? `?${cleaned}` : ''));
    setTimeout(() => (toast.value = null), 6000);
  }
});
</script>

<template>
  <div class="min-h-screen bg-page flex flex-col">
    <AppHeader />
    <main class="flex-1">
      <RouterView />
    </main>
    <AppFooter />
    <CartPopover />

    <!-- Auth-error toast (Steam already linked, etc.) -->
    <Transition>
      <div
        v-if="toast"
        role="alert"
        class="fixed top-6 left-1/2 -translate-x-1/2 z-[60] max-w-[480px] px-5 py-3 rounded-[10px] border border-[var(--color-error)] bg-[#0e1117] text-[var(--color-error)] font-body text-[13px] lg:text-[14px] shadow-2xl"
      >
        {{ toast }}
      </div>
    </Transition>
  </div>
</template>
